import { useState, useEffect } from "react";
import { User, ShoppingBag, Heart, Shield } from "lucide-react";
import {
  Popover,
  PopoverContent,
} from "@/components/ui/popover";
import { useStaticTranslation } from "@/lib/staticTranslations";

interface TourStep {
  icon: typeof User;
  titleKey: string;
  titleFallback: string;
  descKey: string;
  descFallback: string;
}

const userTourSteps: TourStep[] = [
  {
    icon: User,
    titleKey: "personalDetails", titleFallback: "פרטים אישיים",
    descKey: "personalDetailsDesc", descFallback: "כאן תוכל לערוך את הפרטים האישיים שלך, כולל שם, טלפון וכתובת למשלוח.",
  },
  {
    icon: ShoppingBag,
    titleKey: "myOrders", titleFallback: "ההזמנות שלי",
    descKey: "myOrdersDesc", descFallback: "צפה בהיסטוריית ההזמנות שלך ועקוב אחר המשלוחים בקלות.",
  },
  {
    icon: Heart,
    titleKey: "myFavorites", titleFallback: "המועדפים שלי",
    descKey: "myFavoritesDesc", descFallback: "כל המוצרים שסימנת כמועדפים נמצאים כאן. תוכל להוסיף אותם לסל בקלות.",
  },
];

const adminExtraStep: TourStep = {
  icon: Shield,
  titleKey: "adminMessages", titleFallback: "ניהול הודעות",
  descKey: "adminMessagesDesc", descFallback: "כמנהל, תוכל לצפות ולנהל את כל ההודעות שנשלחו דרך טופס יצירת הקשר.",
};

interface ProfileTourProps {
  isAdmin: boolean;
  activeTab: string;
  onComplete: () => void;
}

export const ProfileTour = ({ isAdmin, activeTab, onComplete }: ProfileTourProps) => {
  const [currentTip, setCurrentTip] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const { t } = useStaticTranslation("profileTour");
  
  const tourSteps = isAdmin ? [...userTourSteps, adminExtraStep] : userTourSteps;

  useEffect(() => {
    const tabIndex = ["details", "orders", "favorites", "admin"].indexOf(activeTab);
    if (tabIndex >= 0 && tabIndex < tourSteps.length) {
      setCurrentTip(tabIndex);
    }
  }, [activeTab, tourSteps.length]);

  useEffect(() => {
    const getCurrentAnchorElement = () => {
      const tabs = ["details", "orders", "favorites"];
      if (isAdmin) tabs.push("admin");
      return tabs[currentTip];
    };
    
    const element = document.getElementById(`tour-anchor-${getCurrentAnchorElement()}`);
    setAnchorElement(element);
  }, [currentTip, isAdmin]);

  const handleNavigation = () => {
    if (currentTip === tourSteps.length - 1) {
      setIsOpen(false);
      onComplete();
    } else {
      setCurrentTip(currentTip + 1);
    }
  };

  if (!isOpen || !anchorElement) {
    return null;
  }

  const rect = anchorElement.getBoundingClientRect();

  return (
    <div
      style={{
        position: "fixed",
        top: rect.top + rect.height / 2,
        left: rect.left - 320,
        zIndex: 100,
      }}
    >
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverContent
          className="max-w-[280px] py-3 shadow-lg"
          side="left"
          showArrow={true}
          align="center"
          sideOffset={20}
        >
          <div className="space-y-3" dir="rtl">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                {(() => {
                  const IconComponent = tourSteps[currentTip].icon;
                  return <IconComponent className="h-5 w-5 text-primary" />;
                })()}
              </div>
              <div className="flex-1 space-y-1">
                <p className="text-[13px] font-medium">
                  {t(tourSteps[currentTip].titleKey, tourSteps[currentTip].titleFallback)}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t(tourSteps[currentTip].descKey, tourSteps[currentTip].descFallback)}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {currentTip + 1}/{tourSteps.length}
              </span>
              <button
                className="text-xs font-medium text-primary hover:underline"
                onClick={handleNavigation}
              >
                {currentTip === tourSteps.length - 1 ? t("done", "סיימתי") : t("next", "הבא")}
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
